import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import {
  Receipt, CheckCircle, PaperPlaneTilt, MapPin, CircleNotch, ChatCircleDots,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Ready-to-Invoice card.
 *
 * Every load that has a driver-uploaded POD-family document (POD, BOL,
 * Weight Ticket, Scale Ticket) but no invoice_id yet is money sitting
 * in a filing cabinet. Surface these front-and-center on the pilot
 * dashboard so ops can click straight into the invoice draft.
 *
 * Also renders an inline "Thank driver" button per row — 1-tap SMS to
 * the driver confirming "got the POD, invoice going out shortly."
 *
 * Auto-hides when the queue is empty so fresh accounts don't render an
 * empty confusing box.
 */
const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch {
    return "";
  }
}

export default function ReadyToInvoiceCard({ compact = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thankingId, setThankingId] = useState(null);
  const [autoSendingId, setAutoSendingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await client.get("/loads/ready-to-invoice?limit=10");
      setRows(data?.loads || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const thankDriver = async (row) => {
    setThankingId(row.id);
    try {
      const { data } = await client.post(`/loads/${row.id}/thank-driver`);
      if (data.status === "sent") {
        toast.success("Thanks SMS sent to driver ✓");
      } else if (data.status === "already_thanked") {
        toast.info("Driver already thanked for this load");
      } else if (data.status === "no_phone") {
        toast.warning("Driver has no phone on file");
      } else {
        toast.warning(`SMS not sent: ${data.reason || data.status}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send thank-you SMS");
    } finally {
      setThankingId(null);
    }
  };

  const autoSend = async (row) => {
    setAutoSendingId(row.id);
    try {
      const { data } = await client.post(
        `/loads/${row.id}/draft-and-send-invoice`,
        { attach_load_docs: true },
      );
      if (data.sent) {
        toast.success(
          `Invoice ${data.invoice?.invoice_number} sent to ${data.to} ✓`,
        );
      } else {
        toast.warning(
          `Draft created but email failed: ${data.send_error || "unknown"}`,
        );
      }
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not draft + send. Check customer email is on file.",
      );
    } finally {
      setAutoSendingId(null);
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div
      data-testid="ready-to-invoice-card"
      className={`rounded-xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.06] to-transparent ${
        compact ? "p-4" : "p-5 sm:p-6"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center">
            <Receipt size={18} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
              Ready to invoice
            </div>
            <div className="font-display text-lg font-semibold text-white leading-tight">
              <span data-testid="ready-to-invoice-count" className="tabular-nums">
                {rows.length}
              </span>{" "}
              load{rows.length === 1 ? "" : "s"} · POD received
            </div>
          </div>
        </div>
        <Link
          to="/app/invoices"
          data-testid="ready-to-invoice-open-invoices"
          className="shrink-0 text-xs text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
        >
          Open Invoices →
        </Link>
      </div>

      <div className="space-y-2" data-testid="ready-to-invoice-list">
        {rows.map((row) => {
          const thanked = !!row.driver_thanked_at;
          const rowKey = `ready-invoice-row-${row.id}`;
          return (
            <div
              key={row.id}
              data-testid={rowKey}
              className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
            >
              {/* Load ID + lane */}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-neutral-500 truncate">
                  #{row.load_number || row.id.slice(0, 8)}
                </div>
                <div className="text-sm text-white truncate flex items-center gap-1.5 mt-0.5">
                  <MapPin size={12} className="text-neutral-500 shrink-0" />
                  <span className="truncate">
                    {row.origin || "—"}
                    <span className="text-neutral-600 mx-1">→</span>
                    {row.destination || "—"}
                  </span>
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
                  {row.customer || "Unknown customer"}
                  <span className="text-neutral-700 mx-1.5">·</span>
                  POD {timeAgo(row.pod_uploaded_at)}
                </div>
              </div>

              {/* Rate */}
              <div className="shrink-0 text-right hidden sm:block">
                <div className="text-sm font-mono text-white tabular-nums">
                  {money(row.rate)}
                </div>
              </div>

              {/* Thank driver button */}
              <button
                type="button"
                onClick={() => !thanked && thankDriver(row)}
                disabled={thanked || thankingId === row.id}
                data-testid={`ready-invoice-thank-${row.id}`}
                title={
                  thanked
                    ? "Driver already thanked for this load"
                    : "1-tap thanks SMS to the driver who uploaded the POD"
                }
                className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide transition-colors ${
                  thanked
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default"
                    : "bg-white/[0.03] text-neutral-300 border border-white/10 hover:bg-white/[0.08] hover:text-white"
                } disabled:opacity-50`}
              >
                {thankingId === row.id ? (
                  <><CircleNotch size={11} className="animate-spin" /> Sending</>
                ) : thanked ? (
                  <><CheckCircle size={11} weight="fill" /> Thanked</>
                ) : (
                  <><ChatCircleDots size={11} weight="fill" /> Thank driver</>
                )}
              </button>

              {/* Draft + Send button — one-tap POD → invoice → email */}
              <button
                type="button"
                onClick={() => autoSend(row)}
                disabled={autoSendingId === row.id}
                data-testid={`ready-invoice-autosend-${row.id}`}
                title="One-tap: create the draft AND email it to the customer"
                className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors disabled:opacity-50"
              >
                {autoSendingId === row.id ? (
                  <><CircleNotch size={11} className="animate-spin" /> Sending…</>
                ) : (
                  <><Lightning size={11} weight="fill" /> Draft + Send</>
                )}
              </button>

              {/* Draft-only fallback (opens the invoice page with prefilled form) */}
              <Link
                to={`/app/invoices?load_id=${encodeURIComponent(row.id)}`}
                data-testid={`ready-invoice-draft-${row.id}`}
                title="Draft the invoice and review before sending"
                className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide bg-white/[0.04] border border-white/10 text-neutral-300 hover:bg-white/[0.08] transition-colors"
              >
                <PaperPlaneTilt size={11} weight="fill" /> Draft
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
